import { Box, Divider, IconButton, makeStyles, Paper, Step, StepLabel, Stepper } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import React, { useCallback, useState } from 'react';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import NextIcon from 'components/Icons/NextIcon';
import { goBack } from 'utils/history';
import RequiredFieldsLegend from './RequiredFieldsLegend';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  paper: {
    flex: 1,
    position: 'relative',
    marginBottom: '6em',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    margin: '0',
  },
  paperHeader: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 12,
  },
  divider: {
    marginBottom: 12,
  },
  stepperHeader: {
    padding: '50px 50px 0 50px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  closeButton: {
    position: 'absolute',
    right: 20,
    top: 20,
  },
});

export interface FormStepComponentProps<T, ContextType = {}> {
  onSubmit: SubmitHandler<T>;
  formName: string;
  previousStepData: Partial<T>;
  formContext?: ContextType;
  setFormContext: React.Dispatch<React.SetStateAction<ContextType | undefined>>;
  setNextButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  setDuplicateModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface MultiStepFormStep<T, ContextType = {}> {
  label: string;
  component: React.FC<FormStepComponentProps<T, ContextType>>;
  nextButtonLabel?: string;
  requiredFieldsLegend?: boolean;
  previousStepLabel?: string;
  onClickPrevious?: () => void;
  previousRoute?: string;
  onClickNext?: () => void;
  nextStepIcon?: React.ReactElement;
  previousStepIcon?: React.ReactElement;
  setDuplicateModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface MultiStepFormProps<T, ContextType = {}> {
  backRoute: string;
  backIcon?: React.ReactElement;
  formName: string;
  initialData: Partial<T>;
  initialContext?: ContextType;
  steps: MultiStepFormStep<T, ContextType>[];
  onSubmit: SubmitHandler<T>;
  setDuplicateModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

function MultiStepForm<T extends FieldValues, ContextType = {}>({
  initialData,
  initialContext,
  steps,
  backRoute,
  backIcon,
  onSubmit,
  formName,
  setDuplicateModalOpen,
}: MultiStepFormProps<T, ContextType>): React.ReactElement | null {
  const classes = useStyles();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [previousStepData, setPreviousStepData] = useState(initialData);
  const [formContext, setFormContext] = useState<ContextType | undefined>(initialContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const history = useHistory();

  const ActiveStepComponent = steps[activeStep].component;
  const activeOnSubmit = useCallback(
    async (dataToSubmit: any) => {
      if (activeStep < steps.length - 1) {
        setPreviousStepData((data: any) => ({
          ...data,
          ...dataToSubmit,
        }));
        setActiveStep(activeStep + 1);
      } else {
        setIsSubmitting(true);
        try {
          await onSubmit({
            ...previousStepData,
            ...dataToSubmit,
          });
          setIsSubmitting(false);
        } catch (e) {
          setIsSubmitting(false);
        }
      }
    },
    [previousStepData, setPreviousStepData, activeStep, setActiveStep, onSubmit, steps.length],
  );

  const defaultPreviousButtonLabel = t('multiStepForm.previousStep');
  const defaultNextButtonLabel =
    activeStep === steps.length - 1 ? t('multiStepForm.create') : t('multiStepForm.nextStep');
  const defaultNextStepIcon = activeStep === steps.length - 1 ? <AddCircleOutlineOutlinedIcon /> : <NextIcon />;

  const defaultOnClickPrevious = () => {
    setActiveStep(activeStep - 1);
    setNextButtonDisabled(false);
  };

  return (
    <>
      <Paper elevation={2} className={classes.paper}>
        <div className={classes.paperHeader}>
          <Stepper className={classes.stepperHeader} alternativeLabel nonLinear>
            {steps.map((step, index: number) => (
              <Step key={step.label} active={activeStep >= index}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => goBack(backRoute, history)}>
            <Close />
          </IconButton>
        </div>
        <Divider className={classes.divider} variant="middle" />
        <Box mb={4} display="flex" flex={1} flexDirection="column">
          {React.createElement(ActiveStepComponent, {
            onSubmit: activeOnSubmit,
            previousStepData,
            formContext,
            setFormContext,
            setNextButtonDisabled,
            formName,
            setDuplicateModalOpen,
          })}
        </Box>
        {steps[activeStep].requiredFieldsLegend && <RequiredFieldsLegend />}
      </Paper>
      <BottomButtonBar
        disablePrevious={activeStep === 0}
        previousStepLabel={steps[activeStep].previousStepLabel ?? defaultPreviousButtonLabel}
        previousStepIcon={steps[activeStep].previousStepIcon ?? backIcon}
        disableNext={isSubmitting || isNextButtonDisabled}
        nextStepIcon={steps[activeStep].nextStepIcon ?? defaultNextStepIcon}
        nextColor="default"
        nextStepLabel={steps[activeStep].nextButtonLabel ?? defaultNextButtonLabel}
        formName={formName}
        onClickPrevious={steps[activeStep].onClickPrevious ?? defaultOnClickPrevious}
        onClickNext={steps[activeStep].onClickNext}
        hideBackButton={activeStep === 0}
      />
    </>
  );
}
function assertFC<P>(component: React.FC<P>): asserts component is React.FC<P> {
  // We don't need to do anything here because the assertion happens
  // on the type level - we need to pass a valid React component
}
assertFC(MultiStepForm);

export default MultiStepForm;
